<template>
  <v-container fluid class="enrollment-list pa-0">
    <v-data-table :headers="headers"
                  :items="enrollments"
                  :footer-props="{itemsPerPageOptions: [10, 20, 50], showFirstLastPage: true}"
                  multi-sort
                  :search="search"
                  fixed-header
                  :show-select="!hideSelect"
                  class="enrollment-list--table"
                  v-bind="$attrs">
      <template #header.data-table-select>
        <v-simple-checkbox :value="selectedItemIds.length === enrollments.length"
                           @input="selectedAllToggled"
                           :ripple="false"></v-simple-checkbox>
      </template>
      <template #item.data-table-select="{item, select}">
        <v-simple-checkbox :value="selectedItemIds.indexOf(item.id) !== -1"
                           @input="itemSelected(item)"
                           :key="item.id"
                           :ripple="false"></v-simple-checkbox>
      </template>
      <template #item.create_at="{value}">
        {{ $parseDate(value) }}
      </template>
      <template #item.name="{item}">
        {{ `${item.account.surname} ${item.account.given_name}` }}
      </template>
      <template #item.account.last_enrollment_status="{value}">
        {{
          {
            null: 'N/A',
            1: $t('admin_enrollment.accepted'),
            '-1': $t('admin_enrollment.rejected'),
          }[value]
        }}
      </template>
      <template #item.event.name="{value}">
        {{ $sanitizeHtml(value) }}
      </template>
      <template #item.session.order_no="{item}">
        <div v-if="item.session">
          <div><b>{{ item.session.name }}</b></div>
          <div>{{ $parseDate(item.session.start, 'd/M/y, T') }} - {{ $parseDate(item.session.end, 'T') }}</div>
        </div>
      </template>
      <template #item.series.order_no="{item}">
        <div v-if="item.series">
          <div><b>{{ item.series.name }}</b></div>
        </div>
      </template>
      <template #item.meta="{item}">
        {{ getItemMeta(item) }}
      </template>
      <template #item.status="{value}">
        {{
          {
            0: $t('admin_enrollment.pending'),
            1: $t('admin_enrollment.accepted'),
            "-1": $t('admin_enrollment.rejected'),
          }[value]
        }}
      </template>
      <template #item.notifications="{item, value}">
        <v-btn icon
               v-if="value.length > 0"
               @click="showEnrollmentNotifications(item)">
          <v-icon>fas fa-paper-plane</v-icon>
        </v-btn>
      </template>
      <template v-slot:item.actions="{item}">
        <v-row>
          <v-col>
            <font-awesome-icon icon="edit"
                               @click="editItem(item)"></font-awesome-icon>
          </v-col>
        </v-row>
      </template>
    </v-data-table>

    <EnrollmentNotificationRecordModal :enrollment-id="showingEnrollmentId"
                                       :shown="enrollmentNotificationModalShown"
                                       @cancel="enrollmentNotificationModalShown = false"></EnrollmentNotificationRecordModal>
  </v-container>
</template>

<script>
import {computed, onMounted, ref, watch} from '@vue/composition-api'
import _ from "lodash";
import EnrollmentNotificationRecordModal from "@/components/event/enrollment/EnrollmentNotificationRecordModal";

export default {
  name: 'EnrollmentList',
  components: {EnrollmentNotificationRecordModal},
  props: {
    search: String,
    eventId: String,
    sessionId: String,
    seriesId: String,
    userId: String,
    refreshTrigger: Boolean,
    feeTiers: String,
    hideSelect: Boolean,
  },
  setup(props, {root, emit}) {
    const refresh = function () {
      getEnrollments();
    };
    onMounted(() => {
      refresh();
    });

    watch(() => props.refreshTrigger, (newValue) => {
      if (newValue) {
        refresh();
        emit('update:refresh-trigger', false);
      }
    })

    // Filter
    const filterOptions = computed(() => {
      return [
        {text: root.$t('admin_company.name_en'), value: 'name_en', contains: true},
        {text: root.$t('admin_company.name_zh'), value: 'name_zh', contains: true},
      ]
    })

    // Data Table
    const headers = ref([
      {text: root.$t('admin_enrollment.enrolled_at'), value: 'create_at'},
      {text: root.$t('admin_enrollment.account'), value: 'name'},
      {text: root.$t('admin_user.serial_no'), value: 'account.id'},
      {text: root.$t('my_account.mobile'), value: 'account.mobile'},
      {text: root.$t('admin_enrollment.company'), value: 'account.company.name'},
      {text: root.$t('admin_enrollment.last_enrollment'), value: 'account.last_enrollment_status'},
      {text: root.$t('admin_enrollment.event'), value: 'event.name'},
      {text: root.$t('admin_enrollment.session'), value: 'session.order_no'},
      {text: root.$t('admin_enrollment.series'), value: 'series.order_no'},
      {text: root.$t('admin_enrollment.meta'), value: 'meta'},
      {text: root.$t('admin_enrollment.status'), value: 'status'},
      {text: root.$t('admin_enrollment.email_status'), value: 'notifications'},

      // {text: '', value: 'actions', sortable: false},
    ]);

    const enrollments = ref([]);
    const getEnrollments = function () {
      root.$callAPI('hkiarace/admin/event/enrollment/', {
        event_id: props.eventId,
        session_id: props.sessionId,
        series_id: props.seriesId,
        user_id: props.userId,
      }, function (response) {
        enrollments.value = response.body.enrollments;
      });
    };
    const getItemMeta = function (item) {
      if (props.feeTiers) {
        let meta = JSON.parse(item.meta);
        let fee_tiers = JSON.parse(props.feeTiers);
        let result = '';

        fee_tiers.forEach((t) => {
          if (meta[t['id']]) {
            result += root.$localeGet(t, 'name') + ': ' + meta[t['id']];
          }
        });

        return result;
      }
    };

    const selectedAllToggled = function () {
      if (selectedItemIds.value.length !== enrollments.value.length) {
        selectedItemIds.value = enrollments.value.map((e) => e.id);
      } else {
        selectedItemIds.value = [];
      }
    }
    const itemSelected = function (item) {
      let idx = selectedItemIds.value.indexOf(item.id);

      if (idx === -1) {
        selectedItemIds.value = _.union(selectedItemIds.value, [item.id]);
      } else {
        let array = selectedItemIds.value;
        // _.remove(array, (i) => {
        //   return i === item.id;
        // });
        array.splice(idx, 1)
        selectedItemIds.value = array;
      }
    };
    const selectedItemIds = ref([]);
    watch(() => selectedItemIds.value, (newValue) => {
      emit('input', newValue);
    });

    const showingEnrollmentId = ref(null);
    const enrollmentNotificationModalShown = ref(false);
    const showEnrollmentNotifications = function (item) {
      showingEnrollmentId.value = item.id;
      enrollmentNotificationModalShown.value = true;
    };


    const editItem = function (item) {
      emit('edit', item);
    };

    return {
      filterOptions,

      headers,
      enrollments,

      getItemMeta,

      selectedAllToggled,
      itemSelected,
      selectedItemIds,

      showingEnrollmentId,
      enrollmentNotificationModalShown,
      showEnrollmentNotifications,

      editItem,
    }
  }
}
</script>

<style lang="less">
.enrollment-list {
  display: flex;
  flex-direction: column;
  height: 100%;

  .enrollment-list--table {
    flex: 1 1 0;
    display: flex;
    flex-direction: column;

    .v-data-table__wrapper {
      flex: 1 1 0;
    }

    .v-data-footer {
      margin-right: 0 !important;
    }
  }
}
</style>
